import {
	CognitoIdentityProviderClient,
	SignUpCommand,
	ConfirmSignUpCommand,
	ForgotPasswordCommand,
	ConfirmForgotPasswordCommand,
	ResendConfirmationCodeCommand,
	InitiateAuthCommand,
	ChangePasswordCommand,
	GlobalSignOutCommand,
	UpdateUserAttributesCommand,
	AuthFlowType
} from '@aws-sdk/client-cognito-identity-provider';
import { cognitoConfig } from '@/config/cognito';

type JwtPayload = {
	sub: string;
	email?: string;
	name?: string;
	exp?: number;
	[i: string]: unknown;
};

export type CognitoAuthResult = {
	idToken: string;
	accessToken: string;
	refreshToken?: string;
	expiresIn?: number;
};

const client = new CognitoIdentityProviderClient({
	region: cognitoConfig.region
});

const parseJwt = (token: string): JwtPayload | null => {
	try {
		const [, payload] = token.split('.');
		const padded = payload.padEnd(payload.length + (4 - (payload.length % 4)) % 4, '=');
		const json = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
		return JSON.parse(json);
	} catch {
		return null;
	}
};

const computeSecretHash = async (username: string): Promise<string | undefined> => {
	if (!cognitoConfig.clientSecret) return undefined;
	const encoder = new TextEncoder();
	const keyData = encoder.encode(cognitoConfig.clientSecret);
	const data = encoder.encode(username + cognitoConfig.clientId);
	const cryptoKey = await crypto.subtle.importKey(
		'raw',
		keyData,
		{ name: 'HMAC', hash: 'SHA-256' },
		false,
		['sign']
	);
	const signature = await crypto.subtle.sign('HMAC', cryptoKey, data);
	const bytes = new Uint8Array(signature);
	let binary = '';
	for (let i = 0; i < bytes.byteLength; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return btoa(binary);
};

export const cognitoService = {
	signUp: async (email: string, password: string, name?: string, locale?: string): Promise<void> => {
		const secretHash = await computeSecretHash(email);
		const attributes: Array<{ Name: string; Value: string }> = [
			{ Name: 'email', Value: email },
			...(name ? [{ Name: 'name', Value: name }] : []),
			...(locale ? [{ Name: 'locale', Value: locale }] : [])
		];
		const cmd = new SignUpCommand({
			ClientId: cognitoConfig.clientId,
			Username: email,
			Password: password,
			...(secretHash ? { SecretHash: secretHash } : {}),
			UserAttributes: attributes
		});
		await client.send(cmd);
	},

	confirmSignUp: async (email: string, code: string): Promise<void> => {
		const secretHash = await computeSecretHash(email);
		const cmd = new ConfirmSignUpCommand({
			ClientId: cognitoConfig.clientId,
			Username: email,
			ConfirmationCode: code,
			...(secretHash ? { SecretHash: secretHash } : {})
		});
		await client.send(cmd);
	},

	resendConfirmation: async (email: string): Promise<void> => {
		const secretHash = await computeSecretHash(email);
		const cmd = new ResendConfirmationCodeCommand({
			ClientId: cognitoConfig.clientId,
			Username: email,
			...(secretHash ? { SecretHash: secretHash } : {})
		});
		await client.send(cmd);
	},
	forgotPassword: async (email: string): Promise<void> => {
		const secretHash = await computeSecretHash(email);
		const cmd = new ForgotPasswordCommand({
			ClientId: cognitoConfig.clientId,
			Username: email,
			...(secretHash ? { SecretHash: secretHash } : {})
		});
		await client.send(cmd);
	},
	confirmForgotPassword: async (email: string, code: string, newPassword: string): Promise<void> => {
		const secretHash = await computeSecretHash(email);
		const cmd = new ConfirmForgotPasswordCommand({
			ClientId: cognitoConfig.clientId,
			Username: email,
			ConfirmationCode: code,
			Password: newPassword,
			...(secretHash ? { SecretHash: secretHash } : {})
		});
		await client.send(cmd);
	},

	signIn: async (email: string, password: string): Promise<CognitoAuthResult> => {
		const secretHash = await computeSecretHash(email);
		const cmd = new InitiateAuthCommand({
			AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
			ClientId: cognitoConfig.clientId,
			AuthParameters: {
				USERNAME: email,
				PASSWORD: password,
				...(secretHash ? { SECRET_HASH: secretHash } : {})
			}
		});
		const res = await client.send(cmd);
		const auth = res.AuthenticationResult;
		if (!auth || !auth.IdToken || !auth.AccessToken) {
			throw new Error('Falha na autenticação');
		}
		return {
			idToken: auth.IdToken,
			accessToken: auth.AccessToken,
			refreshToken: auth.RefreshToken,
			expiresIn: auth.ExpiresIn
		};
	},

	changePassword: async (accessToken: string, previousPassword: string, proposedPassword: string): Promise<void> => {
		const cmd = new ChangePasswordCommand({
			AccessToken: accessToken,
			PreviousPassword: previousPassword,
			ProposedPassword: proposedPassword
		});
		await client.send(cmd);
	},

	refresh: async (refreshToken: string, username?: string): Promise<CognitoAuthResult> => {
		let secretHash: string | undefined = undefined;
		if (username) {
			secretHash = await computeSecretHash(username);
		}
		const cmd = new InitiateAuthCommand({
			AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
			ClientId: cognitoConfig.clientId,
			AuthParameters: {
				REFRESH_TOKEN: refreshToken,
				...(secretHash ? { SECRET_HASH: secretHash, USERNAME: username as string } : {})
			}
		});
		const res = await client.send(cmd);
		const auth = res.AuthenticationResult;
		if (!auth || !auth.IdToken || !auth.AccessToken) {
			throw new Error('Falha ao renovar sessão');
		}
		return {
			idToken: auth.IdToken,
			accessToken: auth.AccessToken,
			refreshToken: refreshToken,
			expiresIn: auth.ExpiresIn
		};
	},

	globalSignOut: async (accessToken: string): Promise<void> => {
		const cmd = new GlobalSignOutCommand({
			AccessToken: accessToken
		});
		await client.send(cmd);
	},

	updateUserAttributes: async (accessToken: string, attributes: Array<{ Name: string; Value: string }>): Promise<void> => {
		const cmd = new UpdateUserAttributesCommand({
			AccessToken: accessToken,
			UserAttributes: attributes
		});
		await client.send(cmd);
	},

	parseJwt
};
